import React, { createContext, useContext, useState } from 'react';
import { RecentCall } from '../models/RecentCall';
import { TelephonyWidgetContainer } from '../models/TelephonyWidgetContainer';
import { DefaultCallActionState, defaultCallActionStateValue } from './types';

interface TelephonyContextType {
  selectedRecentCall: RecentCall;
  setSelectedRecentCall: React.Dispatch<React.SetStateAction<RecentCall>>;

  agentVoiceConnection: connect.BaseConnection | null;
  setAgentVoiceConnection: React.Dispatch<React.SetStateAction<connect.BaseConnection | null>>;

  isTelephonyWidgetOpen: boolean;
  setIsTelephonyWidgetOpen: React.Dispatch<React.SetStateAction<boolean>>;

  telephonyStatusBarOpen: boolean;
  setTelephonyStatusBarOpen: React.Dispatch<React.SetStateAction<boolean>>;

  activeTelephonyWidgetContainer: TelephonyWidgetContainer;
  setActiveTelephonyWidgetContainer: React.Dispatch<React.SetStateAction<TelephonyWidgetContainer>>;

  thirdPartyVoiceConnection: connect.VoiceConnection | null;
  setThirdPartyVoiceConnection: React.Dispatch<React.SetStateAction<connect.VoiceConnection | null>>;

  voiceContact: connect.Contact | null;
  setVoiceContact: React.Dispatch<React.SetStateAction<connect.Contact | null>>;

  isAgentMute: boolean;
  setIsAgentMute: React.Dispatch<React.SetStateAction<boolean>>;

  isAgentHold: DefaultCallActionState;
  setIsAgentHold: React.Dispatch<React.SetStateAction<DefaultCallActionState>>;
}

const TelephonyContext = createContext<TelephonyContextType>({
  selectedRecentCall: { phoneNumber: '', index: 0 },
  setSelectedRecentCall: () => {},
  agentVoiceConnection: null,
  setAgentVoiceConnection: () => {},
  isTelephonyWidgetOpen: false,
  setIsTelephonyWidgetOpen: () => {},
  telephonyStatusBarOpen: false,
  setTelephonyStatusBarOpen: () => {},
  activeTelephonyWidgetContainer: TelephonyWidgetContainer.OUTBOUND,
  setActiveTelephonyWidgetContainer: () => {},
  thirdPartyVoiceConnection: null,
  setThirdPartyVoiceConnection: () => {},
  voiceContact: null,
  setVoiceContact: () => {},
  isAgentMute: false,
  setIsAgentMute: () => {},
  isAgentHold: defaultCallActionStateValue,
  setIsAgentHold: () => {}
});

export const useTelephonyContext = (): TelephonyContextType => useContext(TelephonyContext);

interface TelephonyProviderProps {
  children: React.ReactNode;
}

export const TelephonyProvider: React.FC<TelephonyProviderProps> = ({ children }) => {
  const [selectedRecentCall, setSelectedRecentCall] = useState<RecentCall>({ phoneNumber: '', index: 0 });
  const [agentVoiceConnection, setAgentVoiceConnection] = useState<connect.BaseConnection | null>(null);
  const [isTelephonyWidgetOpen, setIsTelephonyWidgetOpen] = useState<boolean>(false);
  const [telephonyStatusBarOpen, setTelephonyStatusBarOpen] = useState<boolean>(false);
  const [activeTelephonyWidgetContainer, setActiveTelephonyWidgetContainer] = useState<TelephonyWidgetContainer>(
    TelephonyWidgetContainer.OUTBOUND
  );
  const [thirdPartyVoiceConnection, setThirdPartyVoiceConnection] = useState<connect.VoiceConnection | null>(null);
  const [voiceContact, setVoiceContact] = useState<connect.Contact | null>(null);
  const [isAgentMute, setIsAgentMute] = useState<boolean>(false);
  const [isAgentHold, setIsAgentHold] = useState<DefaultCallActionState>(defaultCallActionStateValue);

  return (
    <TelephonyContext.Provider
      value={{
        selectedRecentCall,
        setSelectedRecentCall,
        agentVoiceConnection,
        setAgentVoiceConnection,
        isTelephonyWidgetOpen,
        setIsTelephonyWidgetOpen,
        telephonyStatusBarOpen,
        setTelephonyStatusBarOpen,
        activeTelephonyWidgetContainer,
        setActiveTelephonyWidgetContainer,
        thirdPartyVoiceConnection,
        setThirdPartyVoiceConnection,
        voiceContact,
        setVoiceContact,
        isAgentMute,
        setIsAgentMute,
        isAgentHold,
        setIsAgentHold
      }}
    >
      {children}
    </TelephonyContext.Provider>
  );
};
